// =============================================================================
// src/features/explore/explore-g26-mode.ts — Estado + camadas do modo "Eleicoes
//   Gerais 2026" do Explorar, delegado pelo ExploreMap (explore-map.ts ja passa de
//   500 linhas; aqui fica toda a logica nova).
//
// Ciclo: o menu de Ano escolhe "2026" -> enter(cargo) baixa o asset principal ->
//   ExploreMap repinta lendo selection() (styling, overlay de votos, card). A
//   camada de zonas TSE ganha a porta de resultado (cor + bloco 2026 no popup) e a
//   camada de locais acompanha o cargo. leave() volta aos anos historicos.
// =============================================================================

import type { Map as LeafletMap } from 'leaflet';
import type { G26CargoCode } from './explore-g26-types';
import type { ExploreG26Store } from './explore-g26-store';
import { G26_DEFAULT_CARGO, g26PartyColor } from './explore-g26-constants';
import { g26Apuracao, g26Entries, g26LeaderParty, g26IndexOfId, type G26Selection } from './explore-g26-derive';
import { buildG26ZoneBlockHtml, g26TurnoutRowsHtml, g26RankingHtml } from './explore-g26-card';
import { ExploreG26LocaisLayer } from './explore-g26-locais-layer';
import type { ExploreTseZoneLayer, TseZoneResultPort } from './explore-tse-zone-layer';
import { escapeHtml } from '../../app/dom';
import { formatVotos } from './explore-vote-anim';

/** Modo 2026: ativo/cargo + camada de locais (criada no 1o toggle). */
export class ExploreG26Mode {
  active = false;
  cargo: G26CargoCode = G26_DEFAULT_CARGO;
  private locaisLayer: ExploreG26LocaisLayer | null = null;

  constructor(private readonly store: ExploreG26Store) {}

  /** Selecao pronta p/ render (null fora do modo ou antes do asset carregar). */
  selection(): G26Selection | null {
    const ds = this.active ? this.store.dataset() : null;
    return ds ? { ds, cargo: this.cargo } : null;
  }

  /**
   * Entra no modo (ou troca de cargo). Resolve false se o asset falhar — o modo
   * historico continua intacto nesse caso.
   */
  async enter(cargo: G26CargoCode): Promise<boolean> {
    try {
      await this.store.loadDataset();
    } catch (error) {
      console.warn(JSON.stringify({ event: 'explore.g26.load-failed', message: String(error) }));
      return false;
    }
    this.active = true;
    this.cargo = cargo;
    return true;
  }

  /** Sai do modo 2026 (volta aos anos historicos). */
  leave(): void {
    this.active = false;
  }

  /** Porta de resultado p/ a camada de zonas TSE (null fora do modo). */
  tseResultPort(): TseZoneResultPort | null {
    const sel = this.selection();
    if (!sel) return null;
    return {
      fillColor: (nrZona) => {
        const apu = g26Apuracao(sel.ds, 'zona', String(nrZona), sel.cargo);
        const sigla = apu ? g26LeaderParty(sel.ds, apu) : null;
        return sigla ? g26PartyColor(sigla) : null;
      },
      popupBlock: (nrZona) => buildG26ZoneBlockHtml(sel, nrZona),
    };
  }

  /** Propaga o estado atual p/ as camadas (zonas TSE e locais). */
  syncLayers(tseZones: ExploreTseZoneLayer | null): void {
    tseZones?.setResultPort(this.tseResultPort());
    void this.locaisLayer?.setCargo(this.cargo);
  }

  /** Liga/desliga a camada de locais de votacao 2026 (cria na 1a vez). */
  toggleLocais(map: LeafletMap, on: boolean): Promise<boolean> {
    this.locaisLayer ??= new ExploreG26LocaisLayer(map, this.store, this.cargo);
    return this.locaisLayer.setVisible(on);
  }

  /**
   * HTML do popup de ranking de um municipio no modo 2026 (overlay de votos ligado):
   * frase do candidato escolhido (ou do 1º) + numeros + ranking completo.
   */
  votePopupHtml(munId: string, munName: string, candidateId: string | null): string | null {
    const sel = this.selection();
    const apu = sel ? g26Apuracao(sel.ds, 'mun', munId, sel.cargo) : null;
    if (!sel || !apu) return null;
    const entries = g26Entries(sel.ds, apu);
    const wanted = g26IndexOfId(candidateId);
    const pick = entries.find((e) => e.cand.ordem === wanted) ?? entries[0];
    const head = pick
      ? `<div class="exp-vp-head"><b>${escapeHtml(pick.cand.nome)}</b> ficou na <b>${pick.posicao}ª</b> posição e obteve <b>${formatVotos(pick.votos)}</b> de ${formatVotos(apu.validos)} votos válidos em <b>${escapeHtml(munName)}</b>.</div>`
      : '';
    return `${head}${g26TurnoutRowsHtml(apu)}${g26RankingHtml(sel.ds, apu, pick?.cand.id ?? null)}`;
  }
}
